import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { PostHogProvider } from "posthog-js/react";
import App from "./App.tsx";
import "./index.css";
import { initSentry } from "./lib/sentry";
import { setupWebVitalsMonitoring, startMemoryMonitoring } from "./utils/performanceMonitoring";
import { initViteConnectionMonitor } from "./utils/viteDevHelper";

const POSTHOG_KEY = "phc_9hVhuhx6DFDOuUU55AzN1z9fswRV2ukbWCrXnra3Utz";
const POSTHOG_HOST = "https://eu.i.posthog.com";

const posthogOptions = {
  api_host: POSTHOG_HOST,
} as const;

// Initialize Sentry
initSentry();

// Initialize Vite connection monitor for dev mode
initViteConnectionMonitor();

// Listen for cookie consent changes and initialize Sentry if needed
window.addEventListener('cookie-consent-updated', (event: Event) => {
  const customEvent = event as CustomEvent;
  if (customEvent.detail?.analytics) {
    initSentry();
  }
});

// Setup performance monitoring
setupWebVitalsMonitoring();

// Start memory monitoring (check every minute)
if (import.meta.env.MODE === "production") {
  startMemoryMonitoring(60000);
}

// Register service worker for smart caching
if ('serviceWorker' in navigator && import.meta.env.MODE === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        // Check for updates periodically
        setInterval(() => {
          registration.update();
        }, 60000); // Check every minute

        // When a new service worker is available
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker available, tell it to activate immediately
                newWorker.postMessage('SKIP_WAITING');
              }
            });
          }
        });
      })
      .catch((error) => {
        console.warn('Service worker registration failed:', error);
      });

    // When the new service worker takes over, reload to get fresh assets
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!refreshing) {
        refreshing = true;
        // Don't auto-reload, let the UpdateAvailablePrompt handle it
      }
    });
  });
}

// Wrap app with Sentry error boundary
const SentryApp = Sentry.withProfiler(App);

createRoot(document.getElementById("root")!).render(
  <PostHogProvider apiKey={POSTHOG_KEY} options={posthogOptions}>
    <SentryApp />
  </PostHogProvider>
);
